import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, IMG } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import BoostDialog from "@/components/BoostDialog";
import { Button } from "@/components/ui/button";
import { Rocket, Zap, TrendingUp, Eye, Star, ArrowRight, Check } from "lucide-react";

export default function BoostPage() {
  const { user, ready, config } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const boostOn = !!config?.boost_enabled;
  const [items, setItems] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ready) return;
    if (!user) { nav("/login"); return; }
    if (!boostOn) { setLoading(false); return; }
    Promise.all([
      api.get("/properties/mine").then(({ data }) => setItems(data)),
      api.get("/payments/boost-packages").then(({ data }) => setTiers(data)),
    ]).finally(() => setLoading(false));
  }, [ready, user, nav, boostOn]);

  // One-tap renewal: if arriving via ?prop=<id> (e.g. from a boost-expiry email),
  // auto-open the boost dialog for that listing once it's loaded.
  useEffect(() => {
    const pid = searchParams.get("prop");
    if (!pid || !items.length) return;
    const match = items.find((p) => p.id === pid);
    if (match) setSelected(match);
  }, [searchParams, items]);

  if (loading) return <div className="p-20 text-center text-gray-500">Loading…</div>;

  if (!boostOn) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="boost-coming-soon">
        <div className="w-20 h-20 mx-auto rounded-2xl bg-[#FFEED9] flex items-center justify-center brand-forest mb-6">
          <Rocket size={36} />
        </div>
        <p className="label-eyebrow text-gray-500 mb-3">Coming soon</p>
        <h1 className="font-display text-5xl sm:text-6xl brand-forest mb-4 tracking-tight">
          Boosts are launching soon.
        </h1>
        <p className="text-lg text-gray-600 max-w-xl mx-auto leading-relaxed mb-10">
          We're finishing our payment setup so you can safely pay in ₹ and boost your listing to the top of local search. Check back in a few days.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button
            onClick={() => nav("/list-property")}
            className="bg-brand-forest hover:bg-[#E64520] text-white rounded-full h-12 px-6 font-semibold"
          >
            List a property
          </Button>
          <Button
            onClick={() => nav("/dashboard")}
            variant="outline"
            className="rounded-full h-12 px-6 border-brand-subtle"
          >
            Back to dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#FFEED9] to-[#FFF6E9]">
        <div className="absolute inset-0 sunburst opacity-70" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 label-eyebrow text-gray-600 mb-4">
              <Rocket size={13} className="brand-forest" /> Boost your property
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-none brand-forest mb-4 tracking-tight">
              Get seen. <em className="not-italic text-[#0EA5E9]">First</em>.
            </h1>
            <p className="text-lg text-gray-700 max-w-2xl leading-relaxed">
              Boosted listings sit at the top of every search result in their city — earning up to 5× more views and 3× more inquiries. Choose a boost duration, pay once, and go live in seconds.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl">
            {[
              { icon: <Eye size={20} />, big: "5×", small: "more property views" },
              { icon: <TrendingUp size={20} />, big: "3×", small: "more owner inquiries" },
              { icon: <Star size={20} />, big: "#1", small: "spot in city search" },
            ].map((s) => (
              <div key={s.small} className="bg-white/70 backdrop-blur rounded-2xl p-5 border border-brand-subtle flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#FFE0D2] flex items-center justify-center brand-forest">{s.icon}</div>
                <div>
                  <p className="font-display text-3xl brand-forest leading-none">{s.big}</p>
                  <p className="text-sm text-gray-600 mt-1">{s.small}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tier cards */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="label-eyebrow text-gray-500 mb-2">Choose a plan</p>
            <h2 className="font-display text-4xl brand-forest">Boost durations</h2>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {tiers.map((t) => {
            const isBest = t.id === "boost_30";
            return (
              <div
                key={t.id}
                data-testid={`boost-page-tier-${t.id}`}
                className={`relative rounded-3xl p-8 border transition-all ${
                  isBest ? "bg-brand-forest text-white border-transparent shadow-2xl scale-[1.02]" : "bg-white border-brand-subtle hover-lift"
                }`}
              >
                {isBest && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0EA5E9] text-white text-[10px] font-bold tracking-widest uppercase px-4 py-1 rounded-full">Best value</span>
                )}
                <p className={`label-eyebrow mb-3 ${isBest ? "text-white/70" : "text-gray-500"}`}>{t.featured_days}-day boost</p>
                <div className="font-display text-5xl font-bold mb-1">₹{t.amount}</div>
                <p className={`text-sm mb-6 ${isBest ? "text-white/80" : "text-gray-500"}`}>
                  ₹{Math.round(t.amount / t.featured_days)} per day
                </p>
                <ul className="space-y-2 mb-8 text-sm">
                  <li className="flex items-center gap-2"><Check size={14} className={isBest ? "text-white" : "brand-forest"} /> Top of search in your city</li>
                  <li className="flex items-center gap-2"><Check size={14} className={isBest ? "text-white" : "brand-forest"} /> Featured badge on your listing</li>
                  <li className="flex items-center gap-2"><Check size={14} className={isBest ? "text-white" : "brand-forest"} /> Stackable if renewed early</li>
                </ul>
                <p className={`text-xs ${isBest ? "text-white/70" : "text-gray-500"}`}>
                  Pick a property below to apply this tier.
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pick a listing */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="label-eyebrow text-gray-500 mb-2">Your listings</p>
            <h2 className="font-display text-4xl brand-forest">Pick one to boost</h2>
          </div>
          <Button
            onClick={() => nav("/list-property")}
            variant="outline"
            className="rounded-full border-brand-subtle"
          >
            + Add new listing
          </Button>
        </div>
        {items.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-brand-subtle">
            <p className="text-gray-500 mb-4">You haven't listed a property yet.</p>
            <Button onClick={() => nav("/list-property")} className="bg-brand-forest hover:bg-[#E64520] text-white rounded-full">
              Create your first listing
            </Button>
          </div>
        ) : (
          <div className="grid gap-3">
            {items.map((p) => (
              <div key={p.id} className="bg-white border border-brand-subtle rounded-2xl p-4 flex items-center gap-4 hover-lift" data-testid={`boost-listing-${p.id}`}>
                <img
                  src={p.image_paths?.[0] ? IMG(p.image_paths[0]) : "https://images.pexels.com/photos/16110999/pexels-photo-16110999.jpeg?w=200"}
                  alt="" className="w-20 h-20 object-cover rounded-xl"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-gray-900 line-clamp-1">{p.title}</p>
                    {p.is_featured && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFEED9] text-[#FF5A3D] text-[10px] font-bold tracking-widest uppercase">
                        <Star size={10} className="fill-[#FF5A3D]" /> Featured
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 line-clamp-1">{p.locality}, {p.city} • ₹{p.price.toLocaleString("en-IN")}{p.listing_type === "rent" ? "/mo" : ""}</p>
                </div>
                <Button
                  data-testid={`boost-page-select-${p.id}`}
                  onClick={() => setSelected(p)}
                  className="bg-brand-forest hover:bg-[#E64520] text-white rounded-full h-11 px-5"
                >
                  <Zap size={16} className="mr-1" />
                  {p.is_featured ? "Extend" : "Boost"} <ArrowRight size={14} className="ml-1" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      <BoostDialog property={selected} open={!!selected} onOpenChange={(o) => !o && setSelected(null)} />
    </div>
  );
}
